import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Copy, Share2, ArrowLeftRight, MoreVertical, Trash2, EyeOff, Eye } from 'lucide-react';
import { formatDateRange } from '../utils/formatters';

/**
 * Week header component displaying week number, date range, mileage stats, and actions
 */
const WeekHeader = ({
  weekIndex,
  weekDays,
  completedMileage,
  plannedMileage,
  completedRuns,
  totalRuns,
  darkMode,
  theme,
  verticalLayout,
  onDuplicateClick,
  onShareClick,
  onDeleteClick,
  onHideClick,
  onUnhideClick,
  isEmpty,
  isHidden,
  isReorderMode,
  onToggleReorder
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMenuOpen]);
  return (
    <div className={
      (verticalLayout ? 'sticky top-0 z-10 ' : '') +
      (darkMode ? 'bg-slate-800 rounded-t-2xl p-4 border-b border-slate-700' : 'bg-white rounded-t-2xl p-4 border-b border-slate-200')
    }>
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-3">
          <div className={'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white ' + theme.primary}>
            {weekIndex + 1}
          </div>
          <div>
            <h2 className="text-xl font-bold">
              {formatDateRange(weekDays[0], weekDays[6])}
            </h2>
            <div className="text-sm">
              <span className={'font-semibold ' + theme.text}>{completedMileage}</span>
              <span className={darkMode ? 'text-slate-500 font-normal' : 'text-slate-400 font-normal'}>/{plannedMileage}mi</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Desktop buttons - reorder and duplicate only */}
          <button
            onClick={onToggleReorder}
            className={
              'hidden md:flex p-2 rounded-xl transition-colors ' +
              (isReorderMode
                ? (darkMode ? theme.primary + ' text-white' : theme.primary + ' text-white')
                : (darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-slate-100 hover:bg-slate-200'))
            }
            title={isReorderMode ? "Exit reorder mode" : "Reorder runs"}
          >
            <ArrowLeftRight className="w-4 h-4" />
          </button>
          <button
            onClick={onDuplicateClick}
            className={darkMode ? 'p-2 rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors' : 'p-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors'}
            title="Duplicate week"
            disabled={isReorderMode}
          >
            <Copy className="w-4 h-4" />
          </button>

          {/* Three-dot menu (desktop and mobile) */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={darkMode ? 'p-2 rounded-xl bg-slate-700 hover:bg-slate-600 transition-colors' : 'p-2 rounded-xl bg-slate-100 hover:bg-slate-200 transition-colors'}
              title="More options"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {/* Dropdown menu */}
            {isMenuOpen && (
              <div className={
                'absolute right-0 mt-2 w-48 rounded-xl shadow-lg border z-30 overflow-hidden ' +
                (darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')
              }>
                {/* Mobile only: Reorder option */}
                <button
                  onClick={() => {
                    onToggleReorder();
                    setIsMenuOpen(false);
                  }}
                  className={
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors md:hidden ' +
                    (isReorderMode
                      ? (darkMode ? 'bg-slate-700 text-white font-semibold' : 'bg-slate-100 text-slate-900 font-semibold')
                      : (darkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-50 text-slate-700'))
                  }
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  <span className="text-sm">{isReorderMode ? "Exit Reorder Mode" : "Reorder Runs"}</span>
                </button>

                {/* Share option (all screen sizes) */}
                <button
                  onClick={() => {
                    onShareClick();
                    setIsMenuOpen(false);
                  }}
                  disabled={isReorderMode}
                  className={
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ' +
                    (isReorderMode
                      ? 'opacity-50 cursor-not-allowed'
                      : (darkMode ? 'hover:bg-slate-700 text-slate-200' : 'hover:bg-slate-50 text-slate-700'))
                  }
                >
                  <Share2 className="w-4 h-4" />
                  <span className="text-sm">Share Week</span>
                </button>

                {/* Hide option (all screen sizes, only when empty) */}
                {isEmpty && (
                  <button
                    onClick={() => {
                      onHideClick();
                      setIsMenuOpen(false);
                    }}
                    disabled={isReorderMode}
                    className={
                      'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-t ' +
                      (isReorderMode
                        ? 'opacity-50 cursor-not-allowed'
                        : (darkMode ? 'hover:bg-slate-700 text-slate-200 border-slate-700' : 'hover:bg-slate-50 text-slate-700 border-slate-200'))
                    }
                  >
                    <EyeOff className="w-4 h-4" />
                    <span className="text-sm">Hide Week</span>
                  </button>
                )}

                {/* Delete option (all screen sizes) */}
                <button
                  onClick={() => {
                    onDeleteClick();
                    setIsMenuOpen(false);
                  }}
                  disabled={isReorderMode}
                  className={
                    'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors border-t ' +
                    (isReorderMode
                      ? 'opacity-50 cursor-not-allowed'
                      : (darkMode ? 'hover:bg-red-900 text-red-400 border-slate-700' : 'hover:bg-red-50 text-red-600 border-slate-200'))
                  }
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm">Clear All Runs</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isReorderMode ? (
        <div className={`p-3 rounded-xl border-2 ${darkMode ? 'bg-blue-950 border-blue-700 text-blue-200' : 'bg-blue-50 border-blue-300 text-blue-800'}`}>
          <div className="flex items-center gap-2">
            <div className="text-lg">🔄</div>
            <div className="flex-1">
              <p className="font-semibold text-xs">Reorder Mode Active</p>
              <p className="text-[11px] opacity-90">
                <span className="hidden md:inline">Click and drag runs to reorder them within this week.</span>
                <span className="inline md:hidden">Long-press and drag runs to reorder them.</span>
              </p>
            </div>
            <button
              onClick={onToggleReorder}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                darkMode
                  ? 'bg-blue-800 hover:bg-blue-700 text-blue-100'
                  : 'bg-blue-200 hover:bg-blue-300 text-blue-900'
              }`}
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className={darkMode ? 'h-2 bg-slate-700 rounded-full' : 'h-2 bg-slate-200 rounded-full'}>
          <div
            className={'h-full rounded-full transition-all ' + theme.primary}
            style={{ width: totalRuns > 0 ? ((completedRuns / totalRuns) * 100) + '%' : '0%' }}
          />
        </div>
      )}
    </div>
  );
};

WeekHeader.propTypes = {
  weekIndex: PropTypes.number.isRequired,
  weekDays: PropTypes.arrayOf(PropTypes.instanceOf(Date)).isRequired,
  completedMileage: PropTypes.string.isRequired,
  plannedMileage: PropTypes.string.isRequired,
  completedRuns: PropTypes.number.isRequired,
  totalRuns: PropTypes.number.isRequired,
  darkMode: PropTypes.bool.isRequired,
  theme: PropTypes.shape({
    primary: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  verticalLayout: PropTypes.bool.isRequired,
  onDuplicateClick: PropTypes.func.isRequired,
  onShareClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onHideClick: PropTypes.func.isRequired,
  onUnhideClick: PropTypes.func,
  isEmpty: PropTypes.bool,
  isHidden: PropTypes.bool,
  isReorderMode: PropTypes.bool,
  onToggleReorder: PropTypes.func.isRequired,
};

export default WeekHeader;
